import { useEffect, useState, useRef } from '@wordpress/element';
import { trackEvent } from '../utils/analytics';

const AD_SIZES = {
    hero: 'Recommended: 2100 × 500',
    portrait: 'Recommended: 1080 × 1920',
    landscape: 'Recommended: 1920 × 1080',
};

export default function AdSlot({
    placement,
    type = 'landscape',
    label = 'Advertisement',
}) {
    const [ad, setAd] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Create an anchor point for the viewport visibility observer
    const adRef = useRef(null);
    const viewTrackedRef = useRef(false);

    const slotType = AD_SIZES[type] ? type : 'landscape';

    useEffect(() => {
        if (!placement) {
            return;
        }

        async function loadAd() {
            setIsLoading(true);
            viewTrackedRef.current = false; // Reset tracker flag for a new placement payload

            try {
                const restUrl =
                    window.bajantubeMusic?.restUrl || '/wp-json/bajantube/v1';

                const response = await fetch(
                    `${restUrl}/ads?placement=${encodeURIComponent(placement)}&v=${Date.now()}`,
                    {
                        cache: 'no-store',
                    }
                );

                const result = await response.json();

                if (!response.ok || !result.active) {
                    setAd(null);
                    return;
                }

                setAd(result);

                // 1. Telemetry: Data payload found and injected into DOM framework
                trackEvent('ad_impression', {
                    ad_id: result.id || 'unknown',
                    ad_title: result.title || 'Untitled Ad',
                    ad_placement: placement,
                    ad_type: slotType
                });
            } catch (error) {
                console.error('Failed to load ad:', error);
                setAd(null);
            } finally {
                setIsLoading(false);
            }
        }

        loadAd();
    }, [placement]);

    // 2. Telemetry: Watcher logic detecting precisely when the ad hits the viewport
    useEffect(() => {
        if (!ad || !adRef.current || viewTrackedRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // Fire exactly when 50% or more of the element is exposed on screen
                if (entry.isIntersecting && !viewTrackedRef.current) {
                    viewTrackedRef.current = true; // Block duplicate tracking calls

                    trackEvent('ad_view', {
                        ad_id: ad.id || 'unknown',
                        ad_title: ad.title || 'Untitled Ad',
                        ad_placement: placement,
                        ad_type: slotType
                    });

                    observer.disconnect(); // Stop watching once recorded
                }
            },
            { threshold: 0.5 } // Require 50% visibility
        );

        observer.observe(adRef.current);

        return () => observer.disconnect();
    }, [ad, placement]);

    const handleAdClick = () => {
        if (!ad) return;

        trackEvent('ad_click', {
            ad_id: ad.id || 'unknown',
            ad_title: ad.title || 'Untitled Ad',
            ad_placement: placement,
            ad_type: slotType
        });
    };

    const imageUrl =
        placement === 'top_banner'
            ? ad?.image
            : slotType === 'landscape'
                ? ad?.landscapeImage
                : ad?.portraitImage;

    if (placement && ad && imageUrl) {
        return (
            <a
                ref={adRef}
                href={ad.linkUrl || '#'}
                className={`bjnt-ad-slot bjnt-ad-slot--${slotType} is-active`}
                onClick={handleAdClick}
                target="_blank"
                rel="noreferrer sponsored"
                aria-label={ad.title || label}
            >
                <img
                    src={imageUrl}
                    alt={ad.title || label}
                />
            </a>
        );
    }

    return (
        <div
            className={`bjnt-ad-slot bjnt-ad-slot--${slotType} ${isLoading ? 'is-loading' : ''
                }`}
        >

        </div>
    );
}

function getAdSize(type) {
    return AD_SIZES[type] || AD_SIZES.inline;
}